/**
 * Universal XML Parser
 * Parsira bilo koji XML i vraća strukturu pogodnu za prikaz i mapiranje
 */

import * as XLSX from 'xlsx';
import { stripLeadingDescriptionCss } from '../../supabase/functions/_shared/descriptionCss';

export interface XmlNode {
  name: string;
  path: string;           // XPath-like path: "root/product/name"
  value?: string;         // Vrijednost ako je leaf node
  attributes?: Record<string, string>;
  children?: XmlNode[];
  isArray?: boolean;      // Da li se ovaj element ponavlja
  sampleValues?: string[]; // Primjeri vrijednosti (max 3)
  count?: number;         // Koliko puta se ponavlja
}

export interface ParsedFeed {
  rootElement: string;
  recordElement: string;  // Element koji predstavlja jedan record (npr. "product", "item")
  totalRecords: number;
  structure: XmlNode;
  fields: FlatField[];    // Flat lista svih polja za mapiranje
  sampleRecords: any[];   // Prvih N recorda kao objekti
}

export interface FlatField {
  path: string;           // "product/name" ili "product/prices/price"
  displayPath: string;    // Za prikaz u UI
  sampleValues: string[];
  isAttribute?: boolean;
  attributeOf?: string;
}

/**
 * Parse XLS/XLSX file content (ArrayBuffer) and return a ParsedFeed-compatible structure.
 * First row is treated as headers.
 */
export function parseXlsStructure(buffer: ArrayBuffer, sheetName?: string): ParsedFeed {
  const workbook = XLSX.read(buffer, { type: 'array' });
  const effectiveSheetName = sheetName || workbook.SheetNames[0];
  if (!effectiveSheetName) {
    throw new Error('Invalid XLS/XLSX: No sheets found');
  }

  const sheet = workbook.Sheets[effectiveSheetName];
  if (!sheet) {
    throw new Error(`Invalid XLS/XLSX: Sheet not found: ${effectiveSheetName}`);
  }

  const rows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet, {
    defval: '',
    raw: false,
  });

  const normalizedRows = rows.map(r => {
    const obj: Record<string, any> = {};
    Object.entries(r).forEach(([k, v]) => {
      const key = String(k || '').trim();
      if (!key) return;
      obj[key] = v;
    });
    return obj;
  });

  const allKeys = new Set<string>();
  normalizedRows.forEach(r => Object.keys(r).forEach(k => allKeys.add(k)));
  const keys = Array.from(allKeys);

  const fields: FlatField[] = keys.map(key => {
    const sampleValues = normalizedRows
      .slice(0, 50)
      .map(r => (r[key] === undefined || r[key] === null ? '' : String(r[key]).trim()))
      .filter(Boolean)
      .slice(0, 3);

    return {
      path: key,
      displayPath: key,
      sampleValues,
    };
  });

  return {
    rootElement: 'xlsx',
    recordElement: 'row',
    totalRecords: normalizedRows.length,
    structure: {
      name: 'xlsx',
      path: 'xlsx',
      count: 1,
      children: [
        {
          name: 'row',
          path: 'xlsx/row',
          isArray: true,
          count: normalizedRows.length,
        },
      ],
    },
    fields,
    // For Excel: return ALL rows since we can't re-parse from xmlContent
    sampleRecords: normalizedRows,
  };
}

/**
 * Check if content is JSON (starts with { or [)
 */
export function isJsonContent(content: string): boolean {
  const trimmed = content.trim();
  return trimmed.startsWith('{') || trimmed.startsWith('[');
}

/**
 * Parse JSON string and return ParsedFeed-compatible structure.
 * Auto-detects the array of records.
 */
export function parseJsonStructure(jsonString: string): ParsedFeed {
  const data = JSON.parse(jsonString);

  let records: any[] = [];
  let recordElement = 'item';
  const rootElement = 'json';

  if (Array.isArray(data)) {
    records = data;
    recordElement = 'item';
  } else if (typeof data === 'object' && data !== null) {
    // Find the largest array of objects
    let maxLen = 0;
    for (const [key, value] of Object.entries(data)) {
      if (Array.isArray(value) && value.length > maxLen && value.length > 0 && typeof value[0] === 'object') {
        records = value as any[];
        recordElement = key;
        maxLen = value.length;
      }
    }
    if (records.length === 0) {
      throw new Error('No array of records found in JSON');
    }
  } else {
    throw new Error('Invalid JSON: expected object or array');
  }

  // Extract flat fields from records
  const fields: FlatField[] = [];
  if (records.length > 0) {
    extractJsonFields(records[0], '', fields, records);
  }

  return {
    rootElement,
    recordElement,
    totalRecords: records.length,
    structure: {
      name: rootElement,
      path: rootElement,
      count: 1,
      children: [{
        name: recordElement,
        path: `${rootElement}/${recordElement}`,
        isArray: true,
        count: records.length,
      }],
    },
    fields,
    sampleRecords: records.slice(0, 50),
  };
}

/**
 * Recursively extract flat fields from a JSON object
 */
function extractJsonFields(obj: any, prefix: string, fields: FlatField[], allRecords: any[]) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return;

  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}/${key}` : key;

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      // Nested object - recurse
      extractJsonFields(value, path, fields, allRecords);
    } else {
      // Leaf value (string, number, boolean, null, array)
      const sampleValues = allRecords
        .slice(0, 3)
        .map(r => {
          let v = r;
          for (const part of path.split('/')) {
            v = v?.[part];
          }
          return v === undefined || v === null ? '' : String(v);
        })
        .filter(Boolean);

      fields.push({
        path,
        displayPath: path,
        sampleValues,
      });
    }
  }
}

/**
 * Parsira XML string i vraća strukturu
 */
export function parseXmlStructure(xmlString: string): ParsedFeed {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

  // Check for parse errors
  const parseError = xmlDoc.querySelector('parsererror');
  if (parseError) {
    throw new Error('Invalid XML: ' + parseError.textContent);
  }

  const root = xmlDoc.documentElement;
  const rootElement = root.tagName;

  // Detect record element (first repeating child)
  const recordElement = detectRecordElement(root);
  const records = root.getElementsByTagName(recordElement);
  const totalRecords = records.length;

  // Build structure from first record
  const structure = buildNodeStructure(root, '', 0);

  // Extract flat fields for mapping
  const fields = extractFlatFields(records[0] || root, '');

  // Get sample records as objects (više za pregledavanje)
  const sampleRecords = extractSampleRecords(records, 50);

  return {
    rootElement,
    recordElement,
    totalRecords,
    structure,
    fields,
    sampleRecords,
  };
}

/**
 * Detektuje koji element predstavlja jedan record
 * Traži prvi element koji se ponavlja, čak i ako je ugniježđen
 */
function detectRecordElement(root: Element): string {
  // Funkcija koja rekurzivno traži ponavljajući element
  function findRepeatingElement(element: Element, depth: number = 0): string | null {
    // Ograniči dubinu pretrage na 3 nivoa
    if (depth > 3) return null;

    const childCounts = new Map<string, number>();

    Array.from(element.children).forEach(child => {
      const count = childCounts.get(child.tagName) || 0;
      childCounts.set(child.tagName, count + 1);
    });

    // Traži element koji se ponavlja više od 1 put
    for (const [tagName, count] of childCounts.entries()) {
      if (count > 1) {
        return tagName;
      }
    }

    // Ako nema ponavljajućih, traži dublje u svakom child elementu
    for (const child of Array.from(element.children)) {
      const found = findRepeatingElement(child, depth + 1);
      if (found) return found;
    }

    return null;
  }

  const found = findRepeatingElement(root);
  if (found) return found;

  // Fallback: ako nema ponavljajućih, uzmi prvi grandchild ili child
  const firstChild = root.children[0];
  if (firstChild?.children.length > 0) {
    return firstChild.children[0].tagName;
  }

  return root.children[0]?.tagName || root.tagName;
}

/**
 * Gradi tree strukturu XML-a
 * Rekurzivno prolazi kroz sve nivoe bez ograničenja
 */
function buildNodeStructure(
  element: Element,
  parentPath: string,
  depth: number = 0
): XmlNode {
  const name = element.tagName;
  const path = parentPath ? `${parentPath}/${name}` : name;

  // Limit depth to prevent infinite recursion (max 10 levels deep)
  if (depth > 10) {
    return {
      name,
      path,
      count: 1,
      value: '...',
    };
  }

  const node: XmlNode = {
    name,
    path,
    count: 1,
  };

  // Extract attributes
  if (element.attributes.length > 0) {
    node.attributes = {};
    Array.from(element.attributes).forEach(attr => {
      node.attributes![attr.name] = attr.value;
    });
  }

  // Check if leaf node (has text content, no element children)
  const elementChildren = Array.from(element.children);
  if (elementChildren.length === 0) {
    node.value = element.textContent?.trim() || '';
    node.sampleValues = [node.value].filter(Boolean);
  } else {
    // Process children - group by tag name
    const childMap = new Map<string, Element[]>();
    elementChildren.forEach(child => {
      const existing = childMap.get(child.tagName) || [];
      existing.push(child);
      childMap.set(child.tagName, existing);
    });

    node.children = [];
    childMap.forEach((children, _tagName) => {
      // Build structure from first instance of each unique child tag
      const childNode = buildNodeStructure(children[0], path, depth + 1);
      childNode.isArray = children.length > 1;
      childNode.count = children.length;

      // Collect sample values from all instances if it's an array of leaf nodes
      if (children.length > 1 && !childNode.children) {
        childNode.sampleValues = children
          .slice(0, 3)
          .map(c => c.textContent?.trim() || '')
          .filter(Boolean);
      }

      node.children!.push(childNode);
    });
  }

  return node;
}

/**
 * Izvlači flat listu polja za mapiranje
 */
function extractFlatFields(element: Element, prefix: string): FlatField[] {
  const fields: FlatField[] = [];

  function traverse(el: Element, path: string) {
    const currentPath = path ? `${path}/${el.tagName}` : el.tagName;
    // displayPath je jednak currentPath - ne uklanjamo root jer se koristi za mapiranje
    const displayPath = currentPath;

    // Add attributes as fields
    Array.from(el.attributes).forEach(attr => {
      fields.push({
        path: `${currentPath}/@${attr.name}`,
        displayPath: `${displayPath}/@${attr.name}`,
        sampleValues: [attr.value],
        isAttribute: true,
        attributeOf: currentPath,
      });
    });

    const elementChildren = Array.from(el.children);

    if (elementChildren.length === 0) {
      // Leaf node - add as field
      fields.push({
        path: currentPath,
        displayPath,
        sampleValues: [el.textContent?.trim() || ''].filter(Boolean),
      });
    } else {
      // Also add parent element as a field (for complex nested structures)
      // This allows mapping entire subtrees like dodatneLastnosti
      fields.push({
        path: currentPath,
        displayPath: displayPath + ' [complex]',
        sampleValues: [`{${elementChildren.length} children}`],
      });

      // Group children by tag name to detect arrays
      const childGroups = new Map<string, Element[]>();
      elementChildren.forEach(child => {
        const existing = childGroups.get(child.tagName) || [];
        existing.push(child);
        childGroups.set(child.tagName, existing);
      });

      // Traverse children, marking arrays
      childGroups.forEach((children, tagName) => {
        const isArray = children.length > 1;
        const child = children[0];
        const childPath = `${currentPath}/${tagName}`;
        const childDisplayPath = isArray
          ? `${currentPath}/${tagName} [×${children.length}]`
          : `${currentPath}/${tagName}`;

        // Add attributes from first child
        Array.from(child.attributes).forEach(attr => {
          // Collect all unique attribute values from array items
          const allValues = isArray
            ? [...new Set(children.map(c => c.getAttribute(attr.name) || ''))].filter(Boolean).slice(0, 5)
            : [attr.value];

          fields.push({
            path: `${childPath}/@${attr.name}`,
            displayPath: `${childDisplayPath}/@${attr.name}`,
            sampleValues: allValues,
            isAttribute: true,
            attributeOf: childPath,
          });
        });

        const grandChildren = Array.from(child.children);
        if (grandChildren.length === 0) {
          // Leaf node - collect sample values from all array items
          const sampleValues = isArray
            ? children.slice(0, 5).map(c => c.textContent?.trim() || '').filter(Boolean)
            : [child.textContent?.trim() || ''].filter(Boolean);

          fields.push({
            path: childPath,
            displayPath: childDisplayPath,
            sampleValues,
          });
        } else {
          // Has children - recurse but only for first instance
          traverse(child, currentPath);
        }
      });
    }
  }

  traverse(element, prefix);
  return fields;
}

/**
 * Izvlači sample recorde kao JS objekte
 */
function extractSampleRecords(records: HTMLCollectionOf<Element>, limit: number): any[] {
  const samples: any[] = [];

  for (let i = 0; i < Math.min(records.length, limit); i++) {
    samples.push(elementToObject(records[i]));
  }

  return samples;
}

/**
 * Pretvara XML element u JS objekt
 */
export function elementToObject(element: Element): Record<string, any> | string {
  const obj: Record<string, any> = {};

  // Add attributes
  const hasAttributes = element.attributes.length > 0;
  Array.from(element.attributes).forEach(attr => {
    obj[`@${attr.name}`] = attr.value;
  });

  // Process children
  const children = Array.from(element.children);

  if (children.length === 0) {
    // Leaf node - check if has attributes
    const textContent = element.textContent?.trim() || '';
    if (hasAttributes) {
      // Return object with attributes and text content
      obj['#text'] = textContent;
      return obj;
    }
    // Simple leaf - return just text
    return textContent;
  }

  // Group children by tag name
  const groups = new Map<string, Element[]>();
  children.forEach(child => {
    const existing = groups.get(child.tagName) || [];
    existing.push(child);
    groups.set(child.tagName, existing);
  });

  groups.forEach((elements, tagName) => {
    if (elements.length === 1) {
      obj[tagName] = elementToObject(elements[0]);
    } else {
      obj[tagName] = elements.map(el => elementToObject(el));
    }
  });

  return obj;
}

/**
 * Opcije za transformaciju polja
 */
export interface FieldTransformOptions {
  mode: 'single' | 'array' | 'split' | 'keyValue' | 'multiField';  // single = prva vrijednost, array = sve vrijednosti, split = razdvoji po delimiteru, keyValue = pretvori array u {key: value} objekt, multiField = kombiniraj više polja
  delimiter?: string;  // za split mode: ',', '|', ';', etc.
  joinWith?: string;   // ako treba spojiti array nazad u string
  keyField?: string;   // za keyValue mode: koje polje koristiti kao ključ (npr. "ime", "naziv")
  valueField?: string; // za keyValue mode: koje polje koristiti kao vrijednost (npr. "vrednost", "value")
  multiFields?: string[]; // za multiField mode: lista dodatnih polja za kombiniranje (npr. ["Slika1", "Slika2", ...])

  // Value transformation options
  transform?: {
    type: 'none' | 'removePrefix' | 'removeSuffix' | 'replace' | 'regex' | 'prepend' | 'append' | 'europeanNumber' | 'stripCss' | 'stripHtml';
    value?: string;      // vrijednost za uklanjanje/dodavanje/zamjenu
    replaceWith?: string; // zamjenski string (za replace/regex)
  };

  // Price adjustment options (applied after transform)
  priceMultiplier?: number;   // e.g., 1.955883 for EUR→KM conversion
  priceAddPercent?: number;   // e.g., 10 for +10% costs
}

/**
 * Izvlači vrijednost iz objekta po path-u
 * Podržava: "name", "prices/price", "attrs/@id"
 */
export function getValueByPath(obj: any, path: string, options?: FieldTransformOptions): any {
  const parts = path.split('/');
  let current = obj;
  const mode = options?.mode || 'single';

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const isLastPart = i === parts.length - 1;

    if (current === null || current === undefined) {
      return undefined;
    }

    if (part.startsWith('@')) {
      // Attribute
      current = current[part];
    } else if (Array.isArray(current)) {
      if (isLastPart && mode === 'array') {
        // Collect all values from array
        current = current.map(item => item?.[part]).filter(v => v !== undefined && v !== null);
      } else {
        // Get from first element
        current = current[0]?.[part];
      }
    } else {
      current = current[part];
    }
  }

  // Handle final array result
  if (Array.isArray(current) && mode === 'array') {
    // Flatten if needed and filter empty
    const flattened = current.flat().filter(v => v !== undefined && v !== null && v !== '');
    return options?.joinWith ? flattened.join(options.joinWith) : flattened;
  }

  // Handle keyValue mode - convert array of objects to {key: value} object
  if (Array.isArray(current) && mode === 'keyValue' && options?.keyField && options?.valueField) {
    const result: Record<string, string> = {};
    current.forEach(item => {
      if (item && typeof item === 'object') {
        const key = extractTextFromObject(item[options.keyField!]);
        const value = extractTextFromObject(item[options.valueField!]);
        if (key) {
          result[key] = value || '';
        }
      }
    });
    return result;
  }

  // Handle split mode - split string by delimiter
  if (mode === 'split' && typeof current === 'string' && options?.delimiter) {
    const parts = current.split(options.delimiter).map(s => s.trim()).filter(Boolean);
    return options?.joinWith ? parts.join(options.joinWith) : parts;
  }

  // If result is object, extract text content
  if (typeof current === 'object' && current !== null && !Array.isArray(current)) {
    // For keyValue mode, keep the object as is
    if (mode === 'keyValue') {
      return current;
    }
    // Otherwise extract text
    return extractTextFromObject(current);
  }

  return current;
}

/**
 * Helper: izvuci tekst iz objekta (može biti string ili objekt s #text)
 */
function extractTextFromObject(val: any): string {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val.trim();
  if (typeof val === 'number') return String(val);
  if (typeof val === 'object') {
    if ('#text' in val) return String(val['#text']).trim();
    // Try first non-attribute key
    const keys = Object.keys(val).filter(k => !k.startsWith('@'));
    if (keys.length === 1) return extractTextFromObject(val[keys[0]]);
  }
  return '';
}

/**
 * Skuplja SVE vrijednosti iz array path-a
 * Korisno za slike, kategorije, tagove
 */
export function getAllValuesByPath(obj: any, path: string): string[] {
  const result = getValueByPath(obj, path, { mode: 'array' });
  if (Array.isArray(result)) {
    return result.map(v => String(v));
  }
  if (result !== undefined && result !== null && result !== '') {
    return [String(result)];
  }
  return [];
}

/**
 * Primjenjuje transformaciju vrijednosti prema opcijama
 */
export function applyValueTransform(value: any, transform?: FieldTransformOptions['transform']): any {
  if (!transform || transform.type === 'none' || value === undefined || value === null) {
    return value;
  }

  // Convert to string for text operations
  const strValue = String(value);
  const transformValue = transform.value || '';

  switch (transform.type) {
    case 'removePrefix':
      // Remove prefix if it exists
      if (strValue.startsWith(transformValue)) {
        return strValue.substring(transformValue.length).trim();
      }
      return strValue;

    case 'removeSuffix':
      // Remove suffix if it exists
      if (strValue.endsWith(transformValue)) {
        return strValue.substring(0, strValue.length - transformValue.length).trim();
      }
      return strValue;

    case 'replace':
      // Simple string replacement (all occurrences)
      return strValue.split(transformValue).join(transform.replaceWith || '');

    case 'regex':
      // Regex replacement
      try {
        const regex = new RegExp(transformValue, 'g');
        return strValue.replace(regex, transform.replaceWith || '');
      } catch {
        return strValue; // Invalid regex, return original
      }

    case 'prepend':
      // Add prefix
      return transformValue + strValue;

    case 'append':
      // Add suffix
      return strValue + transformValue;

    case 'europeanNumber':
      // Convert European number format (comma decimal) to US format (dot decimal)
      // Examples: "3,99" -> "3.99", "1.099,00" -> "1099.00", "139,00" -> "139.00"
      // First remove thousands separator (dot), then replace decimal separator (comma) with dot
      const cleanedNumber = strValue
        .replace(/\./g, '')     // Remove thousand separators (dots)
        .replace(',', '.');      // Replace decimal comma with dot
      return cleanedNumber;

    case 'stripCss': {
      // Remove <style> blocks, <link> stylesheets, and style/class attributes
      let result = strValue;
      result = result.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
      result = stripLeadingDescriptionCss(result);
      result = result.replace(/<link[^>]*rel\s*=\s*["']stylesheet["'][^>]*\/?>/gi, '');
      result = result.replace(/\s*style\s*=\s*"[^"]*"/gi, '');
      result = result.replace(/\s*style\s*=\s*'[^']*'/gi, '');
      result = result.replace(/\s*class\s*=\s*"[^"]*"/gi, '');
      result = result.replace(/\s*class\s*=\s*'[^']*'/gi, '');
      return result.replace(/\n\s*\n/g, '\n').trim();
    }

    case 'stripHtml': {
      // Remove ALL HTML tags, return plain text
      let result = strValue;
      result = result.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
      result = stripLeadingDescriptionCss(result);
      result = result.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
      result = result.replace(/<br\s*\/?>/gi, '\n');
      result = result.replace(/<\/p>/gi, '\n\n');
      result = result.replace(/<\/li>/gi, '\n');
      result = result.replace(/<\/h[1-6]>/gi, '\n\n');
      result = result.replace(/<[^>]+>/g, '');
      result = result.replace(/&nbsp;/g, ' ');
      return result.replace(/\n{3,}/g, '\n\n').trim();
    }

    default:
      return value;
  }
}

/**
 * Primjenjuje mapiranje na record i vraća DB-ready objekt
 */
export function applyMapping(
  record: any,
  mappings: Record<string, string>, // { dbField: xmlPath }
  fieldOptions?: Record<string, FieldTransformOptions>, // { dbField: options }
  transforms?: Record<string, (value: any) => any>
): Record<string, any> {
  const result: Record<string, any> = {};

  Object.entries(mappings).forEach(([dbField, xmlPath]) => {
    if (!xmlPath) return;

    // Get field-specific options
    const options = fieldOptions?.[dbField];
    let value: any;

    // Handle multiField mode - combine multiple fields into array
    if (options?.mode === 'multiField' && options.multiFields?.length) {
      const values: string[] = [];

      // Get primary field value
      const primaryValue = getValueByPath(record, xmlPath, { mode: 'single' });
      if (primaryValue && String(primaryValue).trim()) {
        values.push(String(primaryValue).trim());
      }

      // Get additional field values
      for (const additionalPath of options.multiFields) {
        const additionalValue = getValueByPath(record, additionalPath, { mode: 'single' });
        if (additionalValue && String(additionalValue).trim()) {
          values.push(String(additionalValue).trim());
        }
      }

      value = values.length > 0 ? values : undefined;
    } else {
      value = getValueByPath(record, xmlPath, options);
    }

    // Apply value transformation from options
    if (options?.transform) {
      value = applyValueTransform(value, options.transform);
    }

    // Apply price adjustment (multiplier + add percent)
    if ((options?.priceMultiplier || options?.priceAddPercent) && value !== undefined && value !== null) {
      const num = parseFloat(String(value));
      if (!isNaN(num)) {
        let adjusted = num;
        if (options.priceMultiplier) adjusted *= options.priceMultiplier;
        if (options.priceAddPercent) adjusted *= (1 + options.priceAddPercent / 100);
        value = parseFloat(adjusted.toFixed(2));
      }
    }

    // Apply custom transform if exists
    if (transforms?.[dbField]) {
      value = transforms[dbField](value);
    }

    // Handle nested DB fields (e.g., "meta_data._dobavljac")
    if (dbField.includes('.')) {
      const [parent, child] = dbField.split('.');
      result[parent] = result[parent] || {};
      result[parent][child] = value;
    } else {
      result[dbField] = value;
    }
  });

  return result;
}

/**
 * Parsira XML i primjenjuje mapiranje - vraća array DB-ready objekata
 */
export function parseAndMap(
  xmlString: string,
  mappings: Record<string, string>,
  fieldOptions?: Record<string, FieldTransformOptions>,
  transforms?: Record<string, (value: any) => any>
): any[] {
  const parsed = parseXmlStructure(xmlString);
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

  const records = xmlDoc.getElementsByTagName(parsed.recordElement);
  const results: any[] = [];

  // Clean mappings - remove record element prefix from paths
  // e.g., "izdelek/VendorItemNo." -> "VendorItemNo."
  const cleanedMappings: Record<string, string> = {};
  const recordPrefix = parsed.recordElement + '/';

  Object.entries(mappings).forEach(([dbField, xmlPath]) => {
    if (!xmlPath) return;
    // Remove record element prefix if present
    const cleanPath = xmlPath.startsWith(recordPrefix)
      ? xmlPath.substring(recordPrefix.length)
      : xmlPath;
    cleanedMappings[dbField] = cleanPath;
  });

  // Also clean fieldOptions keys if they reference full paths
  const cleanedFieldOptions: Record<string, FieldTransformOptions> = {};
  if (fieldOptions) {
    Object.entries(fieldOptions).forEach(([dbField, options]) => {
      cleanedFieldOptions[dbField] = options;
    });
  }

  for (let i = 0; i < records.length; i++) {
    const recordObj = elementToObject(records[i]);
    const mapped = applyMapping(recordObj, cleanedMappings, cleanedFieldOptions, transforms);
    results.push(mapped);
  }

  return results;
}
